import {
  doc,
  getDoc,
  Timestamp,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { db } from "../../services/firebase";
import Drill, { HashType } from "@drillflo/drillcore/lib/Drill";
import Collaboration from "@drillflo/drillcore/lib/Collaboration";
import { ViewOptions } from "@drillflo/drillcore/lib/2d/View";

export interface DrillDocumentData {
  title: string;
  id: string;
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
  audioFile: string;
  audioFileName: string;
  snapshotURL: string;
  drillOptions: {
    endzones: boolean;
    hashType: string;
    performerSymbolStyle: string;
    performerCount: number;
  };
  updated: Date;
}

export interface DrillCustomProperties {
  showEndzone: boolean;
  createdAt: string;
  endZones: boolean;
  hashType: string;
  performerSymbolStyle: string;
  createdBy: string;
  snapToGrid: boolean;
  hashMarkType: string;
  audioFile?: string;
  audioFileName?: string;
}

/** Map legacy / empty Firestore values to a valid engine hash type. */
function normalizeHashType(raw: string | undefined): string {
  if (
    !raw ||
    raw === "none" ||
    raw === "standard" ||
    raw === "undefined" ||
    raw === "HIGH_SCHOOL_TEXAS"
  ) {
    return HashType.HIGH_SCHOOL;
  }
  return raw;
}

function applyDrillOptionsToDrill(
  drill: Drill,
  updateViewOptions: (o: Partial<ViewOptions>) => Promise<void>,
  opts: DrillDocumentData["drillOptions"],
  broadcastCustomData: boolean
): void {
  if (!drill.engine) {
    return;
  }
  const ht = normalizeHashType(opts.hashType);
  drill.engine.hashType = ht as never;
  drill.engine.endZones = opts.endzones;
  drill.engine.performerSymbolStyle = opts.performerSymbolStyle as never;

  void updateViewOptions({
    hashType: ht as ViewOptions["hashType"],
    endzones: opts.endzones,
  });

  drill.setMultipleCustomData(
    {
      hashType: ht,
      hashMarkType: ht,
      endZones: opts.endzones,
      showEndzone: opts.endzones,
      performerSymbolStyle: opts.performerSymbolStyle,
    },
    broadcastCustomData
  );
}

interface UseDrillDocumentProps {
  drillId: string | null;
  drill: Drill | null;
  collaboration: Collaboration | null;
  collaborationHistoryLoading: boolean;
  drillModifiedByCollaboration?: number;
  updateViewOptions: (o: Partial<ViewOptions>) => Promise<void>;
}

export const useDrillDocument = ({
  drillId,
  drill,
  collaboration: _collaboration,
  collaborationHistoryLoading: _collaborationHistoryLoading,
  drillModifiedByCollaboration,
  updateViewOptions,
}: UseDrillDocumentProps) => {
  const [drillDocumentData, setDrillDocumentData] =
    useState<DrillDocumentData | null>(null);
  const drillDocumentDataRef = useRef<DrillDocumentData | null>(null);

  useEffect(() => {
    drillDocumentDataRef.current = drillDocumentData;
  }, [drillDocumentData]);

  const fetchDrillDocumentData = useCallback(
    async (callback: (drillDocumentData: DrillDocumentData | null) => void) => {
      if (drillId && drill) {
        const drillProperties =
          drill.allCustomData as unknown as DrillCustomProperties;

        const q = doc(db, "designs", drillId);
        const drillDoc = await getDoc(q);

        if (drillDoc.exists()) {
          const rawData: Record<string, unknown> = {
            id: drillDoc.id,
            ...drillDoc.data(),
          };
          const fb = (rawData.drillOptions ?? {}) as Partial<
            DrillDocumentData["drillOptions"]
          >;

          const hasFbHash =
            fb.hashType != null &&
            String(fb.hashType) !== "" &&
            String(fb.hashType) !== "none";
          const hasFbSymbol =
            fb.performerSymbolStyle != null && String(fb.performerSymbolStyle) !== "";
          const hasFbEnd = typeof fb.endzones === "boolean";

          const createdAt =
            rawData.createdAt instanceof Timestamp
              ? rawData.createdAt
              : drillProperties.createdAt
                ? Timestamp.fromDate(new Date(drillProperties.createdAt))
                : Timestamp.now();

          const firestoreTitle = rawData.title;
          const resolvedTitle =
            typeof firestoreTitle === "string" && firestoreTitle.trim() !== ""
              ? firestoreTitle
              : (drill.title ?? "");

          if (resolvedTitle && drill.title !== resolvedTitle) {
            const prev = drill.disableChangeBroadcast;
            drill.disableChangeBroadcast = true;
            try {
              drill.title = resolvedTitle;
            } finally {
              drill.disableChangeBroadcast = prev;
            }
          }

          const mergedData: DrillDocumentData = {
            title: resolvedTitle,
            id: (rawData.id ?? "") as string,
            createdAt,
            createdBy:
              drillProperties.createdBy ?? (rawData.createdBy as string) ?? "",
            updatedAt: (rawData.updatedAt as Timestamp) ?? Timestamp.now(),
            audioFile:
              drillProperties.audioFile ?? (rawData.audioFile as string) ?? "",
            audioFileName:
              drillProperties.audioFileName ??
              (rawData.audioFileName as string) ??
              "",
            drillOptions: {
              endzones: hasFbEnd
                ? (fb.endzones as boolean)
                : drillProperties.endZones ??
                drillProperties.showEndzone ??
                false,
              hashType: hasFbHash
                ? String(fb.hashType)
                : drillProperties.hashType ??
                drillProperties.hashMarkType ??
                HashType.HIGH_SCHOOL,
              performerSymbolStyle: hasFbSymbol
                ? String(fb.performerSymbolStyle)
                : drillProperties.performerSymbolStyle ?? "DOT",
              performerCount:
                typeof fb.performerCount === "number"
                  ? fb.performerCount
                  : ((rawData.drillOptions as { performerCount?: number } | undefined)
                    ?.performerCount ?? 0),
            },
            snapshotURL: (rawData.snapshotURL as string) ?? "",
            updated: new Date(),
          };

          // Field display prefs: Firestore drillOptions is source of truth
          applyDrillOptionsToDrill(
            drill,
            updateViewOptions,
            mergedData.drillOptions,
            false
          );

          if (mergedData.audioFile || mergedData.audioFileName) {
            drill.setMultipleCustomData(
              {
                audioFile: mergedData.audioFile,
                audioFileName: mergedData.audioFileName,
              },
              false
            );
          }

          callback(mergedData);
          return;
        }
        callback(null);
      }
    },
    [drillId, drill, updateViewOptions]
  );

  const updateDrillDocumentData = useCallback(
    (data: Partial<DrillDocumentData>) => {
      if (!drill) {
        return;
      }

      const prev = drillDocumentDataRef.current;
      let mergedDrillOptions: DrillDocumentData["drillOptions"] | undefined;

      if (data.drillOptions) {
        mergedDrillOptions = prev
          ? { ...prev.drillOptions, ...data.drillOptions }
          : (data.drillOptions as DrillDocumentData["drillOptions"]);
      }

      setDrillDocumentData((p) => {
        if (!p) {
          if (data.drillOptions) {
            return {
              ...(data as DrillDocumentData),
              drillOptions: mergedDrillOptions!,
            };
          }
          return p;
        }
        if (data.drillOptions && mergedDrillOptions) {
          return { ...p, ...data, drillOptions: mergedDrillOptions };
        }
        return { ...p, ...data } as DrillDocumentData;
      });

      if (mergedDrillOptions && drill.engine) {
        applyDrillOptionsToDrill(
          drill,
          updateViewOptions,
          mergedDrillOptions,
          true
        );
        if (drillId) {
          updateDoc(doc(db, "designs", drillId), {
            drillOptions: mergedDrillOptions,
            updatedAt: serverTimestamp(),
          }).catch((err) => {
            console.error("Error persisting drillOptions to Firestore:", err);
          });
        }
      }

      if (data.audioFile !== undefined || data.audioFileName !== undefined) {
        const audioPatch: Record<string, string> = {};
        if (data.audioFile !== undefined) audioPatch.audioFile = data.audioFile;
        if (data.audioFileName !== undefined) {
          audioPatch.audioFileName = data.audioFileName;
        }
        drill.setMultipleCustomData(audioPatch, true);
        if (drillId) {
          updateDoc(doc(db, "designs", drillId), {
            ...audioPatch,
            ...(data.updated !== undefined && { updated: data.updated }),
            updatedAt: serverTimestamp(),
          }).catch((err) => {
            console.error("Error persisting audio to Firestore:", err);
          });
        }
      }
    },
    [drill, drillId, updateViewOptions]
  );

  useEffect(() => {
    fetchDrillDocumentData((d) => {
      setDrillDocumentData(d);
    });
  }, [fetchDrillDocumentData]);

  // After collaboration reloads the drill, re-apply Firestore drillOptions so they
  // stay authoritative for hash / symbol / endzones.
  useEffect(() => {
    if (
      drillModifiedByCollaboration === undefined ||
      drillModifiedByCollaboration < 1
    ) {
      return;
    }
    const opts = drillDocumentDataRef.current?.drillOptions;
    if (!drill?.engine || !opts) {
      return;
    }
    applyDrillOptionsToDrill(drill, updateViewOptions, opts, false);
  }, [
    drillModifiedByCollaboration,
    drill,
    updateViewOptions,
  ]);

  // Serialized drill from collaboration carries its own title (often the core default
  // "My Drill") and overwrites the design doc title from Firestore. Re-apply the
  // Firestore title after each collaboration-driven drill reload, same as drillOptions.
  useEffect(() => {
    if (
      drillModifiedByCollaboration === undefined ||
      drillModifiedByCollaboration < 1 ||
      !drill
    ) {
      return;
    }
    const authoritativeTitle = drillDocumentDataRef.current?.title;
    if (
      typeof authoritativeTitle !== "string" ||
      authoritativeTitle.trim() === ""
    ) {
      return;
    }
    if (drill.title === authoritativeTitle) {
      return;
    }
    const prev = drill.disableChangeBroadcast;
    drill.disableChangeBroadcast = true;
    try {
      drill.title = authoritativeTitle;
    } finally {
      drill.disableChangeBroadcast = prev;
    }
  }, [drillModifiedByCollaboration, drill]);

  const updatePageData = (pageIndex: number, data: unknown) => {
    setDrillDocumentData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
      };
    });
  };

  const handleChangePageDataCounts = (pageIndex: number, counts: number) => {
    updatePageData(pageIndex, { counts });
  };

  const handleChangePageDataTempo = (pageIndex: number, tempo: number) => {
    updatePageData(pageIndex, { tempo });
  };

  return {
    drillDocumentData,
    fetchDrillDocumentData,
    updateDrillDocumentData,
    handleChangePageDataCounts,
    handleChangePageDataTempo,
  };
};
